import { useTranslation } from "react-i18next";
import React, { useState, useEffect, ChangeEvent } from "react";
import {
  FiSearch,
  FiX,
  FiChevronDown,
  FiChevronUp,
  FiDownload,
} from "react-icons/fi";
import * as XLSX from "xlsx";

import {
  PaymentService,
} from "../../services/payment.service";
import { LoadingSpinner } from "../../components/ui";
import Button from "../../components/ui/button/Button";

import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "../../components/ui/table";

const PAGE_SIZES = [5, 10, 25, 50];

// Interface for snackbar state is used for type safety
type SnackbarSeverity = "success" | "error" | "warning" | "info";

// Define TailwindColorClass type for status chip colors
type TailwindColorClass = string;

// Interface for pagination state
interface PaginationState {
  total: number;
  per_page: number;
  current_page: number;
  last_page: number;
  from: number;
  to: number;
}

const AdminPayments: React.FC = () => {
  const { t } = useTranslation("translation");



  // Use AdminExamPayment type which represents the grouped data
  const [payments, setPayments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  // Status filter might need to be adjusted or removed if the backend doesn't support it on this endpoint yet,
  // but looking at valid params for getExamPayments, it accepts search.
  // The current controller implementation doesn't strictly filter by status on the grouped endpoint, 
  // but we can filter client side or just stick to search for now.
  const [status, setStatus] = useState("all");
  const [method, setMethod] = useState("all");
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");
  const [pagination, setPagination] = useState<PaginationState>({
    total: 0,
    per_page: 10,
    current_page: 1,
    last_page: 1,
    from: 0,
    to: 0,
  });

  // Status update dialog
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedSchoolPayment, setSelectedSchoolPayment] = useState<any | null>(null);
  const [newStatus, setNewStatus] = useState("pending");

  // Expanded schools for details view
  const [expandedSchools, setExpandedSchools] = useState<Set<number>>(new Set());

  // Toast notification
  const [toast, setToast] = useState<{
    open: boolean;
    message: string;
    severity: SnackbarSeverity;
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const fetchPayments = async (
    page = 1,
    limit = 10,
    searchQuery = "",
    statusFilter = "all",
    methodFilter = "all"
  ) => {
    setLoading(true);
    setError("");
    try {
      // Use the grouped endpoint
      const response = await PaymentService.getAdminExamPayments(
        page,
        limit,
        searchQuery,
        statusFilter,
        methodFilter
      );


      // The service returns AdminExamPaymentsResponse which has data.data
      // The backend now returns only schools with payments, correctly paginated.
      const actualData = response.data; // This is { data: AdminExamPayment[], pagination: ... }

      setPayments(actualData.data || []);
      setPagination(
        actualData.pagination || {
          total: 0,
          per_page: limit,
          current_page: 1,
          last_page: 1,
          from: 0,
          to: 0,
        }
      );
    } catch (error) {
      console.error("Error fetching payments:", error);
      setError("Failed to load grouped payments. Please try again.");
      setToast({
        open: true,
        message: t("payments.error_fetching"),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Debounced search effect
  useEffect(() => {
    // Skip the first run if we want to avoid double mounting fetch
    // Actually, it's better to just let it run on mount since search is empty initially
    const handler = setTimeout(() => {
      fetchPayments(1, pagination.per_page, search, status, method);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [search, pagination.per_page, status, method]);

  // Handle page change separately
  const handlePageChange = (direction: "prev" | "next") => {
    if (direction === "prev" && pagination.current_page > 1) {
      fetchPayments(pagination.current_page - 1, pagination.per_page, search, status, method);
    } else if (
      direction === "next" &&
      pagination.current_page < pagination.last_page
    ) {
      fetchPayments(pagination.current_page + 1, pagination.per_page, search, status, method);
    }
  };

  const handleLimitChange = (
    event: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>
  ) => {
    const newLimit = parseInt(event.target.value);
    //fetchPayments(1, newLimit, search); // This will be handled by the useEffect watching pagination.per_page if we add it there
    setPagination(prev => ({ ...prev, per_page: newLimit }));
  };

  const openStatusDialog = (paymentGroup: any) => {
    setSelectedSchoolPayment(paymentGroup);
    setNewStatus(paymentGroup.payment_status);
    setStatusDialogOpen(true);
  };

  const handleStatusUpdate = async () => {
    if (!selectedSchoolPayment) return;

    try {
      // Update all payments for this school
      await PaymentService.updateExamPaymentStatus({
        payment_ids: selectedSchoolPayment.payment_ids,
        payment_status: newStatus,
      });

      setToast({
        open: true,
        message: `Successfully updated status for ${selectedSchoolPayment.school_name}`,
        severity: "success",
      });

      // Refresh payments
      fetchPayments(pagination.current_page, pagination.per_page, search, status, method);
      setStatusDialogOpen(false);
    } catch {
      setToast({
        open: true,
        message: t("payments.error_updating_status"),
        severity: "error",
      });
    }
  };

  const toggleExpandSchool = (schoolId: number) => {
    const newExpanded = new Set(expandedSchools);
    if (newExpanded.has(schoolId)) {
      newExpanded.delete(schoolId);
    } else {
      newExpanded.add(schoolId);
    }
    setExpandedSchools(newExpanded);
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      // Fetch all data for the current filters (large limit)
      const response = await PaymentService.getAdminExamPayments(
        1,
        1000, // Large limit to get all filtered data
        search,
        status,
        method
      );

      const allData = response.data.data || [];
      const tableData: (string | number)[][] = [];

      // 1. Title Row
      const statusLabel = status === "all" ? "" : ` - ${status.toUpperCase()}`;
      const title = `TRU NW ADMIN PAYMENTS REPORT - ${pagination.total > 0 ? allData[0]?.exam_session_name : ""} ${statusLabel}`.toUpperCase();

      // 2. Generated Date Row
      const now = new Date();
      const dateStr = `${String(now.getDate()).padStart(2, "0")}/${String(now.getMonth() + 1).padStart(2, "0")}/${now.getFullYear()} ${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
      const generatedDate = `GENERATED: ${dateStr}`;

      tableData.push([title]);
      tableData.push([generatedDate]);
      tableData.push([]); // Spacer

      // 3. Header Row
      const headerRow = [
        "S/N",
        "School Name",
        "Region",
        "Division",
        "Contact",
        "Exam Type(s)",
        "Total Amount",
        "MoMo Fees",
        "Discounts",
        "Total Payable",
        "Status",
        "Method",
        "Transaction ID",
        "Date"
      ];
      tableData.push(headerRow);

      // 4. Data Rows
      let sn = 1;
      allData.forEach((schoolPayment) => {
        const details = schoolPayment.details || [];

        const statuses = new Set(details.map((d: any) => d.payment_status));
        const hasMixedStatuses = statuses.size > 1;

        if (!hasMixedStatuses && details.length > 0) {
          // All same status - One row
          tableData.push([
            sn++,
            schoolPayment.school_name,
            schoolPayment.school_region,
            schoolPayment.school_division,
            schoolPayment.school_contact || "N/A",
            details.map((d: any) => d.exam_type).join(", "),
            schoolPayment.total_amount,
            schoolPayment.total_fees,
            schoolPayment.total_discount,
            schoolPayment.total_payable || 0,
            schoolPayment.payment_status.toUpperCase(),
            schoolPayment.payment_method || "N/A",
            schoolPayment.transaction_id || "N/A",
            schoolPayment.created_at || "N/A"
          ]);
        } else {
          // Mixed statuses or no details - Individual rows
          details.forEach((detail: any) => {
            tableData.push([
              sn++,
              schoolPayment.school_name,
              schoolPayment.school_region,
              schoolPayment.school_division,
              schoolPayment.school_contact || "N/A",
              detail.exam_type || "N/A",
              detail.amount,
              detail.momo_fee,
              detail.discount_amount || 0,
              (detail.amount - (detail.discount_amount || 0)) + detail.momo_fee,
              detail.payment_status.toUpperCase(),
              detail.payment_method || "N/A",
              detail.transaction_id || "N/A",
              detail.created_at || "N/A"
            ]);
          });

          if (details.length === 0) {
            tableData.push([
              sn++,
              schoolPayment.school_name,
              schoolPayment.school_region,
              schoolPayment.school_division,
              schoolPayment.school_contact || "N/A",
              "N/A",
              schoolPayment.total_amount,
              schoolPayment.total_fees,
              schoolPayment.total_discount,
              schoolPayment.total_payable || 0,
              schoolPayment.payment_status.toUpperCase(),
              schoolPayment.payment_method || "N/A",
              schoolPayment.transaction_id || "N/A",
              schoolPayment.created_at || "N/A"
            ]);
          }
        }
      });

      // Create Sheet and Workbook
      const ws = XLSX.utils.aoa_to_sheet(tableData);
      const wb = XLSX.utils.book_new();

      // Merges for Title and Date
      const totalCols = headerRow.length;
      ws["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: totalCols - 1 } }, // Title
        { s: { r: 1, c: 0 }, e: { r: 1, c: totalCols - 1 } }  // Date
      ];

      XLSX.utils.book_append_sheet(wb, ws, "Payments");

      const statusPart = status !== 'all' ? `_${status}` : '';
      const methodPart = method !== 'all' ? `_${method}` : '';
      const searchPart = search ? `_${search.replace(/[^a-z0-9]/gi, '_').toLowerCase()}` : '';
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);

      const fileName = `Admin_Payments${statusPart}${methodPart}${searchPart}_${timestamp}.xlsx`;
      XLSX.writeFile(wb, fileName);

      setToast({
        open: true,
        message: "Excel export successful",
        severity: "success"
      });
    } catch (err) {
      console.error("Export error:", err);
      setToast({
        open: true,
        message: "Failed to export Excel. Please try again.",
        severity: "error"
      });
    } finally {
      setExporting(false);
    }
  };

  const getStatusChipColor = (status: string): TailwindColorClass => {
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100";
      case "pending":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100";
      case "rejected":
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-100";
    }
  };

  return (
    <div className="max-w-8xl mx-auto p-4">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold mb-6 text-gray-900 dark:text-white">
          {t("payments.admin_title")} (Grouped by School)
        </h1>

        {/* Search Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="w-full md:w-1/2">
              <div className="relative">
                <input
                  type="text"
                  className="w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder={t("common.search") + " (School Name, Code...)"}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <div
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400"
                >
                  <FiSearch className="h-5 w-5" />
                </div>
              </div>
            </div>

            <div className="w-full md:w-1/4">
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
                <option value="rejected">Rejected</option>
                <option value="failed">Failed</option>
              </select>
            </div>

            <div className="w-full md:w-1/4">
              <select
                value={method}
                onChange={(e) => setMethod(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                <option value="all">All Methods</option>
                <option value="mobile_money">Mobile Money</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="card">Card</option>
                <option value="cash">Cash</option>
              </select>
            </div>

            <div className="w-full md:w-auto self-end">
              <Button
                variant="outline"
                onClick={handleExportExcel}
                disabled={exporting || payments.length === 0}
                className="w-full flex items-center justify-center gap-2"
              >
                {exporting ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <FiDownload className="h-5 w-5" />
                )}
                Export Excel
              </Button>
            </div>
          </div>
        </div>

        {error ? <div className="text-red-500 mb-4">{error}</div> : null}

        {/* Loading or Content */}
        {loading ? (
          <div className="flex justify-center my-12">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="space-y-4">
            {payments.length === 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 text-center text-gray-500">
                {t("payments.no_payments")}
              </div>
            ) : (
              payments.map((schoolPayment) => {
                const isExpanded = expandedSchools.has(schoolPayment.school_id);
                return (
                  <div
                    key={schoolPayment.school_id}
                    className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden"
                  >
                    <div
                      className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
                      onClick={() => toggleExpandSchool(schoolPayment.school_id)}
                    >
                      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            {schoolPayment.school_name}
                            <span className="text-xs font-normal text-gray-500 border border-gray-200 rounded px-2 py-0.5">
                              {schoolPayment.exam_session_name}
                            </span>
                          </h3>
                          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600 dark:text-gray-400">
                            <span>Region: {schoolPayment.school_region}</span>
                            <span>•</span>
                            <span>Division: {schoolPayment.school_division}</span>
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-6">
                          <div className="text-right">
                            <p className="text-xs text-gray-500 uppercase">Total Amount</p>
                            <p className="font-bold text-gray-900 dark:text-white">
                              {new Intl.NumberFormat("en-US", {
                                style: "currency",
                                currency: "XAF",
                              }).format(schoolPayment.total_amount)}
                            </p>
                            {schoolPayment.total_discount > 0 && (
                              <p className="text-xs text-green-600 dark:text-green-400">
                                -{new Intl.NumberFormat("en-US", {
                                  style: "currency",
                                  currency: "XAF",
                                }).format(schoolPayment.total_discount)} Discount
                              </p>
                            )}
                          </div>

                          <div className="text-right">
                            <p className="text-xs text-gray-500 uppercase">MoMo Fees</p>
                            <p className="font-bold text-gray-900 dark:text-white">
                              {new Intl.NumberFormat("en-US", {
                                style: "currency",
                                currency: "XAF",
                              }).format(schoolPayment.total_fees)}
                            </p>
                          </div>

                          <div className="text-right">
                            <p className="text-xs text-gray-500 uppercase">Total Payable</p>
                            <p className="font-bold text-blue-600 dark:text-blue-400">
                              {new Intl.NumberFormat("en-US", {
                                style: "currency",
                                currency: "XAF",
                              }).format(schoolPayment.total_payable || 0)}
                            </p>
                          </div>

                          <div className="text-right">
                            <p className="text-xs text-gray-500 uppercase">Status</p>
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusChipColor(
                                schoolPayment.payment_status
                              )}`}
                            >
                              {t(`payments.status_${schoolPayment.payment_status}`)}
                            </span>
                          </div>

                          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => openStatusDialog(schoolPayment)}
                              className="px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-sm font-medium transition-colors"
                            >
                              Update Status
                            </button>
                            <button
                              className="p-1 text-gray-400 hover:text-gray-600"
                              onClick={() => toggleExpandSchool(schoolPayment.school_id)}
                            >
                              {isExpanded ? (
                                <FiChevronUp className="h-6 w-6" />
                              ) : (
                                <FiChevronDown className="h-6 w-6" />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {isExpanded && schoolPayment.details && (
                      <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-4">
                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Payment Breakdown</h4>
                        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                          <Table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                            <TableHeader>
                              <TableRow>
                                <TableCell className="py-2">Exam Type</TableCell>
                                <TableCell className="py-2">Amount</TableCell>
                                <TableCell className="py-2">Discount</TableCell>
                                <TableCell className="py-2">Status</TableCell>
                                <TableCell className="py-2">Method</TableCell>
                                <TableCell className="py-2">Transaction ID</TableCell>
                                <TableCell className="py-2">Date</TableCell>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {schoolPayment.details.map((detail: any) => (
                                <TableRow key={detail.id}>
                                  <TableCell className="py-2">{detail.exam_type || detail.exam_registration?.exam_type?.name}</TableCell>
                                  <TableCell className="py-2">
                                    {new Intl.NumberFormat("en-US", {
                                      style: "currency",
                                      currency: "XAF",
                                    }).format(detail.amount)}
                                  </TableCell>
                                  <TableCell className="py-2">
                                    {(detail.discount_amount || 0) > 0 ? (
                                      <div className="flex flex-col">
                                        <span className="text-green-600 dark:text-green-400 font-medium">
                                          -{new Intl.NumberFormat("en-US", {
                                            style: "currency",
                                            currency: "XAF",
                                          }).format(detail.discount_amount || 0)}
                                        </span>
                                        <span className="text-xs text-gray-500">
                                          ({detail.discount_percentage}%)
                                        </span>
                                      </div>
                                    ) : (
                                      <span className="text-gray-400">-</span>
                                    )}
                                  </TableCell>
                                  <TableCell className="py-2">
                                    <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${getStatusChipColor(detail.payment_status)}`}>
                                      {detail.payment_status}
                                    </span>
                                  </TableCell>
                                  <TableCell className="py-2">{detail.payment_method}</TableCell>
                                  <TableCell className="py-2 text-xs font-mono">{detail.transaction_id}</TableCell>
                                  <TableCell className="py-2 text-xs">{detail.created_at}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Pagination */}{payments.length > 0 && (
          <div className="flex flex-col md:flex-row justify-between items-center mt-6 text-sm">
            <div className="flex items-center mb-4 md:mb-0">
              <span className="mr-2 text-gray-700 dark:text-gray-300">
                {t("common.items_per_page")}:
              </span>
              <select
                value={pagination.per_page.toString()}
                onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                  handleLimitChange(e)
                }
                className="border rounded px-2 py-1 bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                {PAGE_SIZES.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center">
              <span className="mr-4 text-gray-700 dark:text-gray-300">
                {pagination.from}-{pagination.to} {t("common.of")}{" "}
                {pagination.total}
              </span>
              <Button
                disabled={pagination.current_page === 1}
                onClick={() => handlePageChange("prev")}
                variant="outline"
                size="sm"
                className="mr-2"
              >
                {t("common.previous")}
              </Button>
              <Button
                disabled={pagination.current_page === pagination.last_page}
                onClick={() => handlePageChange("next")}
                variant="outline"
                size="sm"
              >
                {t("common.next")}
              </Button>
            </div>
          </div>
        )}

        {/* Status Update Dialog */}
        {statusDialogOpen && selectedSchoolPayment && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">
                {t("payments.update_status")}
              </h3>
              <p className="mb-4 text-gray-600 dark:text-gray-400">
                Updating status for <span className="font-semibold">{selectedSchoolPayment.school_name}</span>.
                This will update all {selectedSchoolPayment.payment_ids.length} payments in this group.
              </p>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  New Status
                </label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="pending">{t("payments.status_pending")}</option>
                  <option value="completed">{t("payments.status_completed")}</option>
                  <option value="rejected">{t("payments.status_rejected")}</option>
                </select>
              </div>

              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => setStatusDialogOpen(false)}
                >
                  {t("common.cancel")}
                </Button>
                <Button
                  variant="primary"
                  onClick={handleStatusUpdate}
                >
                  {t("common.save")}
                </Button>
              </div>
            </div>
          </div>
        )}



        {/* Toast Notification */}
        {toast.open && (
          <div className={`fixed bottom-4 right-4 px-6 py-3 rounded-lg shadow-lg text-white ${toast.severity === 'success' ? 'bg-green-600' :
            toast.severity === 'error' ? 'bg-red-600' : 'bg-blue-600'
            }`}>
            {toast.message}
            <button
              onClick={() => setToast({ ...toast, open: false })}
              className="ml-4 font-bold hover:text-gray-200"
            >
              <FiX />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};


export default AdminPayments;
